// apps/web/src/contexts/MediaPreviewContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';

// ---------- Types ----------
export type MediaItem = {
  id: string;
  url?: string;
  file?: File;                  // not serialized
  musicTitle?: string | null;
  musicArtist?: string | null;
  filterId?: string | null;
  videoId?: string | null;  // HLS video ID
  iv?: string | null;  // Initialization Vector (IV)
  isHls?: boolean;    // Flag to determine if the item is an HLS video
};

type MediaContextType = {
  mediaItems: MediaItem[];
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>>;
  updateMediaItem: (item: MediaItem) => void;
  clearMediaItems: () => void;
  addOrReplaceMediaItem: (item: MediaItem) => void;
  removeMediaItem: (id: string) => void;
  updateMediaMusic: (id: string, musicTitle?: string | null, musicArtist?: string | null) => void;
  updateMediaFilter: (id: string, filterId: string | null) => void;
};

// ---------- Context ----------
const MediaPreviewContext = createContext<MediaContextType | undefined>(undefined);

// ---------- Provider ----------
export const MediaPreviewProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  /**
   * Lazy initializer: read once from localStorage on first render.
   * We only store serializable fields; never persist File or blob: URLs.
   */
  const [mediaItems, setMediaItemsState] = useState<MediaItem[]>(() => {
    try {
      const raw = localStorage.getItem('mediaPreviewItems');
      if (!raw) return [];
      const parsed: Array<Pick<MediaItem, 'id' | 'url' | 'musicTitle' | 'musicArtist' | 'filterId'>> = JSON.parse(raw);
      return parsed.map(s => ({
        id: s.id,
        url: s.url,
        musicTitle: s.musicTitle ?? null,
        musicArtist: s.musicArtist ?? null,
        filterId: s.filterId ?? null,
      })) as MediaItem[];
    } catch {
      return [];
    }
  });

  // If you later add async merges, flip this to false until finished.
  const [hydrated, setHydrated] = useState(true);

  /**
   * Single saver: write to storage whenever mediaItems changes.
   * Guarded by `hydrated` to avoid early writes during any async hydration.
   */
  useEffect(() => {
    if (!hydrated) return;
    const serializable = mediaItems.map(({ id, url, musicTitle, musicArtist, filterId }) => ({
      id,
      url,
      musicTitle: musicTitle ?? null,
      musicArtist: musicArtist ?? null,
      filterId: filterId ?? null,
    }));
    localStorage.setItem('mediaPreviewItems', JSON.stringify(serializable));
  }, [mediaItems, hydrated]);

  // ---------- Updaters ----------
  const setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>> = (updater) => {
    setMediaItemsState(updater);
  };

  const addOrReplaceMediaItem = (newItem: MediaItem) => {
  setMediaItemsState(prev => {
    const idx = prev.findIndex(i => i.id === newItem.id);
    if (idx === -1) return [...prev, newItem];
    const next = [...prev];
    next[idx] = { ...prev[idx], ...newItem }; // merge to preserve existing properties
    return next;
  });
};


  const updateMediaItem = (updated: MediaItem) => {
    setMediaItemsState(prev =>
      prev.map(item => (item.id === updated.id ? { ...item, ...updated } : item))
    );
  };

  const updateMediaMusic = (id: string, musicTitle?: string | null, musicArtist?: string | null) => {
    setMediaItemsState(prev =>
      prev.map(item =>
        item.id === id
          ? { ...item, musicTitle: musicTitle ?? null, musicArtist: musicArtist ?? null }
          : item
      )
    );
  };

  const updateMediaFilter = (id: string, filterId: string | null) => {
    setMediaItemsState(prev =>
      prev.map(item => (item.id === id ? { ...item, filterId } : item))
    );
  };

  const clearMediaItems = () => {
    setMediaItemsState([]);
    localStorage.removeItem('mediaPreviewItems');
  };

  const removeMediaItem = (id: string) => {
    setMediaItemsState(prev => prev.filter(m => m.id !== id));
  };

  return (
    <MediaPreviewContext.Provider
      value={{
        mediaItems,
        setMediaItems,
        updateMediaItem,
        clearMediaItems,
        addOrReplaceMediaItem,
        removeMediaItem,
        updateMediaMusic,
        updateMediaFilter,
      }}
    >
      {children}
    </MediaPreviewContext.Provider>
  );
};

// ---------- Hook ----------
export const useMediaPreview = () => {
  const ctx = useContext(MediaPreviewContext);
  if (!ctx) throw new Error('useMediaPreview must be used within MediaPreviewProvider');
  return ctx;
};
